import type {
  CollectionActions,
  CollectionStateSlice,
} from '@freelancer/datastore/core';
import {
  deepSpread,
  mergeDocuments,
  mergeWebsocketDocuments,
  transformIntoDocuments,
  updateWebsocketDocuments,
} from '@freelancer/datastore/core';
import { isDefined } from '@freelancer/utils';
import type {
  EquipmentItemAddResultApi,
  EscrowComLinkConsentResultApi,
  MarketingMobileNumberResultApi,
  PrimaryCurrencyUpdateResultApi,
  UserOperatingAreaUpdateResultApi,
} from 'api-typings/users/users';
import type { EquipmentGroup } from '../equipment-group';
import {
  mergeUpdateSuccessResult,
  transformMarketingMobileNumberAPIResult,
  transformSecurePhone,
  transformUsersSelf,
} from './users-self.transformers';
import type { UsersSelfCollection } from './users-self.types';
import { transformEquipmentGroupItems } from '../equipment-group/equipment-group.transformers';
import { transformOperatingArea } from '../operating-areas/operating-areas.transformers';

// The type guard is needed as this collection's update function
// hits two different endpoints depending on the delta
function isEscrowComLinkConsentResult(
  result: UsersSelfCollection['Backend']['Update']['ReturnType'],
): result is EscrowComLinkConsentResultApi {
  return 'user' in result;
}

function isPrimaryCurrencyUpdateResult(
  result: UsersSelfCollection['Backend']['Update']['ReturnType'],
): result is PrimaryCurrencyUpdateResultApi {
  return 'currency_id' in result;
}

export function isUserOperatingAreaUpdateResult(
  result: UsersSelfCollection['Backend']['Update']['ReturnType'],
): result is UserOperatingAreaUpdateResultApi {
  return 'operating_areas' in result;
}

export function isEquipmentItemAddResultApi(
  result: UsersSelfCollection['Backend']['Update']['ReturnType'],
): result is EquipmentItemAddResultApi {
  return 'equipment_items' in result;
}

/**
 * This works only because we don't set projections on user's updates
 * and so this field will be missing.
 */
function isMarketingMobileNumberResult(
  result: UsersSelfCollection['Backend']['Update']['ReturnType'],
): result is MarketingMobileNumberResultApi {
  return 'marketing_mobile_number' in result;
}

export function usersSelfReducer(
  state: CollectionStateSlice<UsersSelfCollection> = {},
  action: CollectionActions<UsersSelfCollection>,
): CollectionStateSlice<UsersSelfCollection> {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'usersSelf') {
        const { result, ref, order } = action.payload;
        const preferences = result.directory_follow_preferences;
        if (preferences) {
          if (
            !(
              isDefined(preferences.allow_listing) &&
              isDefined(preferences.allow_watch) &&
              isDefined(preferences.allow_seal_price_on_review)
            )
          ) {
            throw new Error(
              'Allow_listing, allow_watch and allow_seal_price_on_review should be non-null for directory_follow_preferences',
            );
          }
        }
        return mergeDocuments<UsersSelfCollection>(
          state,
          transformIntoDocuments([result], transformUsersSelf),
          order,
          ref,
        );
      }
      return state;
    }

    case 'API_UPDATE': {
      if (action.payload.type === 'usersSelf') {
        const { delta, originalDocument, ref } = action.payload;
        const { chosenRole, directoryAndFollowPreferences } = delta;

        if (chosenRole) {
          return mergeWebsocketDocuments<UsersSelfCollection>(
            state,
            [{ ...originalDocument, chosenRole }],
            ref,
          );
        }

        if (directoryAndFollowPreferences) {
          return mergeWebsocketDocuments<UsersSelfCollection>(
            state,
            [
              {
                ...originalDocument,
                directoryAndFollowPreferences,
              },
            ],
            ref,
          );
        }
      }

      return state;
    }

    case 'API_UPDATE_SUCCESS': {
      if (action.payload.type === 'usersSelf') {
        const { result, ref, originalDocument, delta } = action.payload;

        if (result && isMarketingMobileNumberResult(result)) {
          return mergeWebsocketDocuments<UsersSelfCollection>(
            state,
            transformIntoDocuments(
              [result],
              transformMarketingMobileNumberAPIResult,
              originalDocument,
            ),
            ref,
          );
        }

        if (result && isPrimaryCurrencyUpdateResult(result)) {
          // Only merge the populated data to the store
          if (isDefined(delta.primaryCurrency?.code)) {
            return mergeWebsocketDocuments<UsersSelfCollection>(
              state,
              [
                deepSpread(originalDocument, {
                  primaryCurrency: delta.primaryCurrency,
                }),
              ],
              ref,
            );
          }

          return state;
        }

        if (result && isUserOperatingAreaUpdateResult(result)) {
          return mergeWebsocketDocuments<UsersSelfCollection>(
            state,
            [
              deepSpread(originalDocument, {
                operatingAreas: result.operating_areas.map(operatingAreaApi =>
                  transformOperatingArea(operatingAreaApi),
                ),
              }),
            ],
            ref,
          );
        }

        if (result && isEquipmentItemAddResultApi(result)) {
          const updatedItems = result.equipment_items.map(equipmentApi =>
            transformEquipmentGroupItems(equipmentApi),
          );
          const existingEquipmentGroups =
            originalDocument.equipmentGroups ?? [];
          const groupsToUpdate = delta.equipmentGroups ?? [];
          const updatedGroupIds = new Set(
            groupsToUpdate
              .map(group => group?.id)
              .filter((groupId): groupId is number => isDefined(groupId)),
          );

          const userEquipmentGroups = existingEquipmentGroups.map(userGroup =>
            updatedGroupIds.has(userGroup.id)
              ? {
                  ...userGroup,
                  items: updatedItems,
                }
              : userGroup,
          );

          groupsToUpdate.forEach(group => {
            if (!isDefined(group?.id)) {
              return;
            }

            if (
              userEquipmentGroups.some(userGroup => userGroup.id === group.id)
            ) {
              return;
            }

            if (!isDefined(group.name)) {
              return;
            }

            userEquipmentGroups.push({
              id: group.id,
              name: group.name,
              hasUserInput: false,
              isProjectRequired: false,
              isUserRequired: false,
              items: updatedItems,
            } as EquipmentGroup);
          });

          return mergeWebsocketDocuments<UsersSelfCollection>(
            state,
            [
              deepSpread(originalDocument, {
                equipmentGroups: userEquipmentGroups,
              }),
            ],
            ref,
          );
        }

        const usersArray =
          result && isEscrowComLinkConsentResult(result)
            ? [result.user]
            : [result];
        return mergeWebsocketDocuments<UsersSelfCollection>(
          state,
          transformIntoDocuments(
            usersArray,
            mergeUpdateSuccessResult,
            originalDocument,
          ),
          ref,
        );
      }

      return state;
    }

    case 'API_UPDATE_ERROR': {
      if (action.payload.type === 'usersSelf') {
        const { originalDocument, ref, delta } = action.payload;

        // Only allow specific fields to be reverted when update error occurred.
        const revertableFields = [
          'chosenRole',
          'directoryAndFollowPreferences',
        ];

        if (revertableFields.some(field => field in delta)) {
          return mergeWebsocketDocuments<UsersSelfCollection>(
            state,
            transformIntoDocuments([originalDocument], o => o),
            ref,
          );
        }
      }

      return state;
    }

    case 'WS_MESSAGE': {
      const userId = action.payload.toUserId;
      const ref = {
        path: { collection: 'usersSelf', authUid: userId },
      };

      if (action.payload.type === 'emailVerified') {
        return updateWebsocketDocuments<UsersSelfCollection>(
          state,
          [userId],
          usersSelf =>
            deepSpread(usersSelf, {
              status: { emailVerified: true },
            }),
          ref,
        );
      }

      if (action.payload.type === 'emailChangeConfirmed') {
        const { data } = action.payload;
        return updateWebsocketDocuments<UsersSelfCollection>(
          state,
          [userId],
          usersSelf => ({
            ...usersSelf,
            email: data.newEmail,
          }),
          ref,
        );
      }

      if (action.payload.type === 'userStatusUpdate') {
        const { data } = action.payload;
        return updateWebsocketDocuments<UsersSelfCollection>(
          state,
          [userId],
          usersSelf => {
            if (isDefined(data.status.linkedin_connected)) {
              return deepSpread(usersSelf, {
                status: {
                  linkedinConnected: data.status.linkedin_connected,
                },
              });
            }

            if (isDefined(data.status.facebook_connected)) {
              return deepSpread(usersSelf, {
                status: {
                  facebookConnected: data.status.facebook_connected,
                },
              });
            }

            return usersSelf;
          },
          ref,
        );
      }

      if (action.payload.type === 'profileNameUpdate') {
        const { data } = action.payload;
        return updateWebsocketDocuments<UsersSelfCollection>(
          state,
          [userId],
          usersSelf => ({
            ...usersSelf,
            firstName: data.firstName,
            lastName: data.lastName,
          }),
          ref,
        );
      }

      if (action.payload.type === 'updateBillingAgreement') {
        const paymentVerified = !!action.payload.data.token;
        return updateWebsocketDocuments<UsersSelfCollection>(
          state,
          [userId],
          usersSelf => ({
            ...usersSelf,
            status: {
              ...usersSelf.status,
              paymentVerified,
            },
          }),
          ref,
        );
      }

      if (action.payload.type === 'securePhoneUpdated') {
        const securePhoneUpdate = action.payload.data;

        if (securePhoneUpdate.id_verification_required) {
          return state;
        }

        // Only update security phone if not requiring user to ID-verify
        return updateWebsocketDocuments<UsersSelfCollection>(
          state,
          [userId],
          usersSelf => ({
            ...usersSelf,
            securePhone: transformSecurePhone(securePhoneUpdate.secure_phone),
            status: {
              ...usersSelf.status,
              phoneVerified: true,
            },
          }),
          ref,
        );
      }

      return state;
    }
    default:
      return state;
  }
}
